import { FC, useCallback, useMemo, useState } from 'react';

import * as Yup from 'yup'
import {useFormik} from 'formik'

import { useNavigate } from 'react-router-dom';
import { StatusErrorMessage, DateSelector } from '../../../../../components/widgets';
import { doFormSubmit } from '../../../../../components/helpers';

import { format } from 'date-fns';
import { pickBy } from 'lodash';
import { SubmitButtonComponent } from '../../../../../components/layout';

interface IReassignApplicationModel {
  applicationid?: number
  reviewId?: number
  id: number    
  role: string
  inspectionDate: string
}

const initDefaultValues: IReassignApplicationModel = {
  id : 0,    
  role: '',    
  inspectionDate: '',
}

type Props = {
  appID: number
  reviewId?: number
  role: string
  assignees: any[] | null | undefined
  doClose: Function
  assignedId: number
}

const AdminReassignForm : FC<Props> = ({ appID, reviewId, role, assignees, doClose, assignedId }) => {

  const navigate = useNavigate();
  
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const initValues = useMemo(() => {

    let initVals = {...initDefaultValues}
    if (role) {
      Object.assign(initVals, { role });
    }

    if (reviewId && reviewId > 0) {
      Object.assign(initVals, { reviewId });
    } else if (appID) {
      Object.assign(initVals, { applicationid : appID });
    }

    return initVals;

  }, [appID, reviewId, role])

  const formik = useFormik({
      initialValues: initValues,
      validationSchema: Yup.object({
        applicationid: Yup.number().integer()
        .when('reviewId', {
          is: (val: number) => (val === 0),
          then: (schema) => schema.required().moreThan(0, 'Application required')}),                            
        reviewId: Yup.number().integer()
        .when('appid', {
          is: (val: number) => (val === 0),
          then: (schema) => schema.required().moreThan(0, 'Select Inspection Role')}),
        role: Yup.string().trim(),
        id: Yup.number().integer().label('candidate')
        .when('role', {
          is: (val: string) => (val !== 'CAMS Inspector'),
          then: (schema) => schema.required().moreThan(0, 'Select replacement')}),
        inspectionDate: Yup.string().trim().label('Inspection Date')
      }),
      enableReinitialize: true,
      onSubmit: async (values, {setStatus, setSubmitting}) => {
        let payload = {
          ...pickBy(values, (value, key) => (value && (key !== 'role'))),
          id: parseInt(`${values.id}`)
        };

        if (role === 'CAMS Inspector') {
          if (! (values.id > 0 || values.inspectionDate.length > 0)) {
            setStatus('Select new CAMS Inspector or Inspection Date');
            setSubmitting(false);
            return;
          }

          if (values.id === 0 && assignedId) {
            // @ts-ignore
            Object.assign(payload, {id : assignedId});
          }

          if (values.inspectionDate) {
            Object.assign(payload, { inspectionDate: values.inspectionDate });
          }
        }
        await doFormSubmit('ReassignApplicationServlet', payload, setStatus, setSubmitting, () => {
          navigate('/setsco/application-management/applications');
        });            
      }
  })

  const doCancel = () => {
    doClose();
  }

  const onSetSelectedDate = useCallback((dt: any) => {        
    setSelectedDate(dt);
    formik.setFieldValue('inspectionDate', dt ? format(dt, "yyyy-MM-dd'T'HH:mm:ssxx") : '');
    // eslint-disable-next-line
  }, [setSelectedDate]);

  return (
  <>
  <form id='kt_admin_reassign_form' className='form' onSubmit={formik.handleSubmit} noValidate>
    {/* begin::Scroll */}
    <div
      className='d-flex flex-column gap-4 mb-6 px-2'
      id='kt_admin_reassign_scroll'
      data-kt-scroll='true'
      data-kt-scroll-activate='{default: false, lg: true}'
      data-kt-scroll-max-height='auto'
      data-kt-scroll-dependencies='#kt_admin_reassign_header'
      data-kt-scroll-wrappers='#kt_admin_reassign_scroll'
      data-kt-scroll-offset='300px'
    >
      <div className='fv-row mb-4'>
        <label className='required fw-bold fs-6 mb-2'>{`Select new ${role} : `}</label>
        <div className='d-flex fv-row'>
          <select
            className='form-select form-select-solid fw-bold mb-2'
            {...formik.getFieldProps('id')}
            name='id'
            disabled={formik.isSubmitting}
          >
            <option value=''>{`Select ${role} ...`}</option>
            {assignees && assignees.map( (dat:any) => (
              <option key={`assignee${dat.value}`} value={dat.value}>{dat.text}</option>
            ))}
          </select>
        </div>
        {formik.touched.id && formik.errors.id && (
        <div className='fv-plugins-message-container'>
          <div className='fv-help-block'>
            {formik.errors.id}
          </div>
        </div>
        )}
      </div>

      { (role === 'CAMS Inspector') &&
      <div className='fv-row mb-4 col-8'>
        <label className='fw-bold fs-6 mb-2'>Inspection Date :</label>
        <DateSelector
          className='form-control form-control-sm form-control-solid mb-2 py-2 fs-7 fw-bold'
          selected={selectedDate}
          onChange={onSetSelectedDate}
          showShiftSelector={true}  
          dateFormat = 'LL'
        ></DateSelector>
        {formik.touched.inspectionDate && formik.errors.inspectionDate && (
        <div className='fv-plugins-message-container'>
          <div className='fv-help-block'>
            {formik.errors.inspectionDate}
          </div>
        </div>
        )}
      </div>
      }

      <StatusErrorMessage status={formik.status} />
      <SubmitButtonComponent 
        formik={formik}
        onCancel={doCancel}
      />
    </div>
  </form>
  </>
  )
}


export {AdminReassignForm}